// This file provides unified hooks that automatically switch between local and cloud storage
// based on the user's selected storage mode.

import { useStorageMode, getCurrentMode } from './useStorageMode';
import { useOfflineTransactions } from './useOfflineTransactions';
import { DbTransaction, DbCategory } from './useTransactions';
import { useLocalTransactions } from './useLocalTransactions';
import { useTags as useCloudTags, Tag } from './useTags';
import { useLocalTags } from './useLocalTags';
import { useUserProfile as useCloudUserProfile, UserProfile, WeekStart, DefaultPeriod } from './useUserProfile';
import { useLocalUserProfile } from './useLocalUserProfile';
import { useMemo, useEffect, useRef } from 'react';

// Re-export types for convenience
export type { DbTransaction, DbCategory, Tag, UserProfile, WeekStart, DefaultPeriod };
export { weekStartOptions, periodOptions } from './useUserProfile';

// Unified transactions hook
export function useUnifiedTransactions() {
  const { isLocalMode, isCheckingAuth } = useStorageMode();
  
  // Always call both hooks to respect Rules of Hooks
  // Use offline-capable hook for cloud mode
  const cloudData = useOfflineTransactions();
  const localData = useLocalTransactions();
  
  // Track mode changes and trigger refresh
  const prevModeRef = useRef(isLocalMode);
  
  useEffect(() => {
    if (prevModeRef.current !== isLocalMode && !isCheckingAuth) {
      // Mode changed - refresh appropriate data
      if (isLocalMode) {
        localData.fetchTransactions();
      } else {
        cloudData.fetchTransactions();
      }
      prevModeRef.current = isLocalMode;
    }
  }, [isLocalMode, isCheckingAuth, localData, cloudData]);

  // Memoize the result to prevent unnecessary re-renders
  const result = useMemo(() => {
    // If still checking auth, show loading
    if (isCheckingAuth) {
      return {
        transactions: [],
        categories: [],
        loading: true,
        user: null,
        addTransaction: async () => null,
        updateTransaction: async () => false,
        deleteTransaction: async () => false,
        fetchTransactions: () => {},
        fetchCategories: () => {},
        totalIncome: 0,
        totalExpense: 0,
        balance: 0,
        expensesByCategory: {},
        incomesByCategory: {},
      };
    }

    return isLocalMode ? localData : cloudData;
  }, [isCheckingAuth, isLocalMode, localData, cloudData]);

  return result;
}

// Unified tags hook
export function useUnifiedTags() {
  const { isLocalMode, isCheckingAuth } = useStorageMode();
  
  // Always call both hooks
  const cloudData = useCloudTags();
  const localData = useLocalTags();

  const result = useMemo(() => {
    if (isCheckingAuth) {
      return {
        tags: [],
        loading: true,
        user: null,
        addTag: async () => null,
        updateTag: async () => false,
        deleteTag: async () => false,
        refetch: () => {},
      };
    }

    return isLocalMode ? localData : cloudData;
  }, [isCheckingAuth, isLocalMode, localData, cloudData]);

  return result;
}

// Unified user profile hook
export function useUnifiedUserProfile() {
  const { isLocalMode, isCheckingAuth } = useStorageMode();
  
  // Always call both hooks
  const cloudData = useCloudUserProfile();
  const localData = useLocalUserProfile();

  const result = useMemo(() => {
    if (isCheckingAuth) {
      return {
        profile: null,
        loading: true,
        user: null,
        currency: { code: 'KZT', symbol: '₸', nameKey: 'currencies.KZT', locale: 'kk-KZ' },
        updateProfile: async () => false,
        refetch: () => {},
        getWeekStartLabel: () => '',
        getPeriodLabel: () => '',
      };
    }

    return isLocalMode ? localData : cloudData;
  }, [isCheckingAuth, isLocalMode, localData, cloudData]);

  return result;
}

// Auth hook that works in both modes
export function useUnifiedAuth() {
  const { isLocalMode, cloudUser, isCheckingAuth } = useStorageMode();

  const result = useMemo(() => {
    if (isLocalMode) {
      return {
        user: { id: 'local' },
        isReady: true,
        isLocalMode: true,
      };
    }

    return {
      user: cloudUser,
      isReady: !isCheckingAuth,
      isLocalMode: false,
    };
  }, [isLocalMode, cloudUser, isCheckingAuth]);

  return result;
}
